// dependencies
import { useState } from "react"
import Select from "react-select";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";

// api
import { addDocument } from "../../api/firebase/firebase"

// components
import ImageUpload from "../../components/image/ImageUpload"
import PrimaryBtn from "../../components/btns/PrimaryBtn"
import SecondaryBtn from "../../components/btns/SecondaryBtn"

// styles
import styles from "./css/AddProject.module.css";


const AddProject = () => {
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        content: "",
        ytLink: "",
        githubLink: "",
        liveLink: "",
        imgs: [],
        tags: [],
        features: [],
        technologies: [],
        thumbnail: "",
        dateStarted: null,
        dateCompleted: null,
    })

    const handleTagsChange = (selectedOptions) => {
        setFormData({ ...formData, tags: selectedOptions.map(option => option.value) })
    }

    const handleThumbnailUpload = (uploadedThumbnail) => {
        setFormData((prevState) => ({
            ...prevState,
            // expecting only one image for thumbnail
            thumbnail: uploadedThumbnail[0],
        }))
    }

    const handleImagesUpload = (uploadedImages) => {
        setFormData((prevState) => ({
            ...prevState,
            imgs: [...prevState.imgs, ...uploadedImages],
        }))
    }



    // feature helpers
    const addFeature = (feature) => {
        if (feature.trim()) {
            setFormData({ ...formData, features: [...formData.features, feature.trim()] });
        }
    };

    const removeFeature = (index) => {
        const updated = [...formData.features];
        updated.splice(index, 1);
        setFormData({ ...formData, features: updated });
    };

    // technology helpers
    const addTechnologyType = () => {
        setFormData({
            ...formData,
            technologies: [...formData.technologies, { type: "", items: [] }],
        });
    };

    const updateTechnologyType = (index, value) => {
        const updated = [...formData.technologies];
        updated[index].type = value;
        setFormData({ ...formData, technologies: updated });
    };

    const addTechnologyItem = (index, item) => {
        if (!item.trim()) return;
        const updated = [...formData.technologies];
        updated[index].items.push(item.trim());
        setFormData({ ...formData, technologies: updated });
    };

    const removeTechnologyItem = (typeIndex, itemIndex) => {
        const updated = [...formData.technologies];
        updated[typeIndex].items.splice(itemIndex, 1);
        setFormData({ ...formData, technologies: updated });
    };

    const removeTechnologyType = (index) => {
        const updated = [...formData.technologies];
        updated.splice(index, 1);
        setFormData({ ...formData, technologies: updated });
    };



    // handle form submit
    const handleSubmit = async () => {
        //console.log("formData", formData)
        if (
            !formData.title || 
            !formData.description || 
            !formData.content || 
            !formData.tags.length || 
            !formData.thumbnail || 
            !formData.dateStarted ||
            formData.imgs.length === 0 ||
            formData.tags.length === 0 ||
            formData.technologies.length === 0 ||
            formData.features.length === 0 
        ) {
            alert("Please fill all required fields before submitting.")
            return
        }

        try {
            const newProject = {
                ...formData,
                createdAt: new Date().toISOString(),
            }

            // uploda to firestore db
            await addDocument("projects", newProject)

            toast("Project added successfully!", { type: "success" })

            // reset form
            setFormData({
                title: "",
                description: "",
                content: "",
                ytLink: "",
                githubLink: "",
                imgs: [],
                tags: [],
                features: [],
                technologies: [],
                thumbnail: "",
                dateStarted: null,
                dateCompleted: null,
            });
        } catch (error) {
            console.error("Error adding project:", error)
            toast("Error adding project", { type: "error" })
        }
    }

    
    return (
        <div className={styles.formContainer}>
            <div className={styles.form}>
                <div className={styles.addTitle}>Add Project</div>

                {/* Title, Description, Content */}
                <div className={styles.inputContainer}>
                    <label htmlFor="title" className={styles.labelText}>
                        Title <span>*</span>
                    </label>
                    <input
                        className={`${styles.textInput} ${styles.titleText}`}
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                title: e.target.value,
                            })
                        }
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="description" className={styles.labelText}>
                        Description <span>*</span>
                    </label>
                    <textarea
                        className={styles.largeTextInput}
                        name="description"
                        value={formData.description}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                description: e.target.value,
                            })
                        }
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="content" className={styles.labelText}>
                        Content <span>*</span>
                    </label>
                    <textarea
                        className={styles.largeTextInput}
                        name="content"
                        value={formData.content}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                content: e.target.value,
                            })
                        }
                    />
                </div>

                {/* Features */}
                <div className={styles.inputContainer}>
                    <label className={styles.labelText}>
                        Features
                    </label>
                    <div className={styles.flexRow}>
                        <input
                            className={styles.textInput}
                            name="features"
                            placeholder="Add a feature"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    addFeature(e.target.value);
                                    e.target.value = "";
                                }
                            }}
                        />
                    </div>
                    <ul className={styles.tagList}>
                        {formData.features.map((feature, i) => (
                            <li key={i} className={styles.tagItem}>
                                {feature}
                                <button type="button" onClick={() => removeFeature(i)}>
                                    x
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Technologies */}
                <div className={styles.inputContainer}>
                    <label className={styles.labelText}>
                        Technologies
                    </label>
                    {formData.technologies.map((tech, index) => (
                        <div key={index} className={styles.techGroup}>
                            <div className={styles.flexRow}>
                                <input
                                    className={styles.textInput}
                                    name="technologyType"
                                    value={tech.type}
                                    placeholder="Type (e.g. Frontend)"
                                    onChange={(e) =>
                                        updateTechnologyType(index, e.target.value)
                                    }
                                />
                                <SecondaryBtn 
                                    onClick={() => removeTechnologyType(index)} 
                                    text={"Remove Type"} 
                                />
                            </div>

                            <div className={styles.flexRow}>
                                <input
                                    className={styles.textInput}
                                    name="technologyItem"
                                    placeholder="Add item"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            addTechnologyItem(
                                                index,
                                                e.target.value
                                            );
                                            e.target.value = "";
                                        }
                                    }}
                                />
                            </div>

                            <ul className={styles.tagList}>
                                {tech.items.map(
                                    (item, itemIndex) => (
                                        <li key={itemIndex} className={ styles.tagItem }>
                                            {item}
                                            <button
                                                type="button"
                                                onClick={() => removeTechnologyItem(index, itemIndex)}
                                            >
                                                x
                                            </button>
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    ))}

                    <PrimaryBtn 
                        onClick={addTechnologyType}
                        text={"+ Add Technology Type"}
                    />
                </div>

                {/* YouTube, GitHub, Live Link */}
                <div className={styles.inputContainer}>
                    <label htmlFor="ytLink" className={styles.labelText}>
                        YouTube Link
                    </label>
                    <input
                        className={styles.textInput}
                        type="url"
                        name="ytLink"
                        value={formData.ytLink}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                ytLink: e.target.value,
                            })
                        }
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="githubLink" className={styles.labelText}>
                        GitHub Link
                    </label>
                    <input
                        className={styles.textInput}
                        type="url"
                        name="githubLink"
                        value={formData.githubLink}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                githubLink: e.target.value,
                            })
                        }
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="githubLink" className={styles.labelText}>
                        Live Link
                    </label>
                    <input
                        className={styles.textInput}
                        type="url"
                        name="liveLink"
                        value={formData.liveLink}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                liveLink: e.target.value,
                            })
                        }
                    />
                </div>

                {/* Tags */}
                <div className={styles.inputContainer}>
                    <label htmlFor="tags" className={styles.labelText}>
                        Tags <span>*</span>
                    </label>
                    <Select
                        isMulti
                        options={[
                            { value: "Active", label: "Active" },
                            { value: "Completed", label: "Completed" },
                            { value: "Uni Project", label: "Uni Project" },
                            { value: "Personal Project", label: "Personal Project" },
                            { value: "Freelance Project", label: "Freelance Project" },
                        ]}
                        onChange={handleTagsChange}
                    />
                </div>

                {/* Dates */}
                <div className={styles.inputContainer}>
                    <label htmlFor="dateStarted" className={styles.labelText}>
                        Date Started
                    </label>
                    <DatePicker
                        selected={formData.dateStarted}
                        onChange={(date) =>
                            setFormData({
                                ...formData,
                                dateStarted: date,
                            })
                        }
                        dateFormat="yyyy/MM/dd"
                        isClearable
                    />
                </div>

                <div className={styles.inputContainer}>
                    <label htmlFor="dateCompleted" className={styles.labelText}>
                        Date Completed
                    </label>
                    <DatePicker
                        selected={formData.dateCompleted}
                        onChange={(date) =>
                            setFormData({
                                ...formData,
                                dateCompleted: date,
                            })
                        }
                        dateFormat="yyyy/MM/dd"
                        isClearable
                    />
                </div>

                {/* Thumbnail Upload */}
                <div className={styles.inputContainer}>
                    <label htmlFor="thumbnail" className={styles.labelText}>
                        Thumbnail Image <span>*</span>
                    </label>
                    <ImageUpload
                        folderName={formData.title}
                        onImagesUpload={handleThumbnailUpload}
                        isThumbnail={true}
                    />
                </div>

                {/* Image Upload */}
                <div className={styles.inputContainer}>
                    <label htmlFor="imgs" className={styles.labelText}>
                        Project Image(s) <span>*</span>
                    </label>
                    <ImageUpload
                        folderName={formData.title}
                        onImagesUpload={handleImagesUpload}
                    />
                </div>

                {/* Submit */}
                <div className={styles.submitContainer}>
                    <PrimaryBtn 
                        onClick={handleSubmit}
                        text={"Publish"}
                    />
                </div>
            </div>
        </div>
    );
}

export default AddProject
